import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  Divider,
  Drawer,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import PageContainer from '../../../components/PageContainer';
import AppDataTable from '../../../components/AppDataTable';
import {
  actualizarUsuario,
  crearUsuario,
  desactivarUsuario,
  getUsuarios,
} from '../../../services/modules/Usuarios';

const ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'cajero', label: 'Cajero' },
  { value: 'bodega', label: 'Bodega' },
  { value: 'viewer', label: 'Viewer' },
];

const Usuarios = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [processing, setProcessing] = useState(false);

  const [formData, setFormData] = useState({
    id_usuario: null,
    username: '',
    nombre: '',
    rol: 'cajero',
    activo: true,
    pin: '',
  });

  const fetchUsuarios = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getUsuarios();
      setUsuarios(data || []);
    } catch (err) {
      setError(err?.message || 'No pudimos cargar los usuarios.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsuarios();
  }, []);

  const handleOpenPanel = (usuario = null) => {
    if (usuario) {
      setFormData({
        id_usuario: usuario.id_usuario,
        username: usuario.username,
        nombre: usuario.nombre,
        rol: usuario.rol,
        activo: Boolean(usuario.activo),
        pin: '',
      });
    } else {
      setFormData({
        id_usuario: null,
        username: '',
        nombre: '',
        rol: 'cajero',
        activo: true,
        pin: '',
      });
    }
    setDrawerOpen(true);
  };

  const handleClosePanel = () => {
    if (processing) return;
    setDrawerOpen(false);
  };

  const handleSubmit = async () => {
    setProcessing(true);
    setError(null);
    try {
      if (formData.id_usuario) {
        await actualizarUsuario(formData.id_usuario, {
          username: formData.username,
          nombre: formData.nombre,
          rol: formData.rol,
          activo: formData.activo ? 1 : 0,
          ...(formData.pin ? { pin: formData.pin } : {}),
        });
      } else {
        await crearUsuario({
          username: formData.username,
          nombre: formData.nombre,
          rol: formData.rol,
          activo: formData.activo ? 1 : 0,
          pin: formData.pin,
        });
      }
      setDrawerOpen(false);
      await fetchUsuarios();
    } catch (err) {
      setError(err?.message || 'Error al guardar el usuario.');
      console.error(err);
    } finally {
      setProcessing(false);
    }
  };

  const handleDesactivar = async (usuario) => {
    if (!usuario?.id_usuario) return;
    if (!window.confirm(`¿Desactivar usuario ${usuario.username}?`)) return;
    setProcessing(true);
    setError(null);
    try {
      await desactivarUsuario(usuario.id_usuario);
      await fetchUsuarios();
    } catch (err) {
      setError(err?.message || 'Error al desactivar el usuario.');
      console.error(err);
    } finally {
      setProcessing(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: 'username',
        headerName: 'Usuario',
        flex: 1,
        minWidth: 160,
        sortable: true,
      },
      {
        field: 'nombre',
        headerName: 'Nombre',
        flex: 1.2,
        minWidth: 200,
        sortable: true,
      },
      {
        field: 'rol',
        headerName: 'Rol',
        flex: 0.8,
        minWidth: 140,
        sortable: true,
        renderCell: ({ row }) => <Chip size="small" label={row.rol} />,
      },
      {
        field: 'estado',
        headerName: 'Estado',
        flex: 0.8,
        minWidth: 140,
        sortable: true,
        valueGetter: (_value, row) => (row?.activo ? 1 : 0),
        renderCell: ({ row }) =>
          row.activo ? (
            <Chip size="small" color="success" label="Activo" />
          ) : (
            <Chip size="small" color="default" label="Inactivo" />
          ),
      },
      {
        field: 'acciones',
        headerName: 'Acciones',
        minWidth: 220,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" onClick={() => handleOpenPanel(row)}>
              Editar
            </Button>
            <Button
              size="small"
              variant="text"
              color="error"
              disabled={!row.activo}
              onClick={() => handleDesactivar(row)}
            >
              Desactivar
            </Button>
          </Stack>
        ),
      },
    ],
    [processing]
  );

  return (
    <PageContainer title="Usuarios">
      <Stack spacing={2}>
        {error && <Alert severity="error">{error}</Alert>}

        <Stack
          direction={isXs ? 'column' : 'row'}
          spacing={2}
          justifyContent="space-between"
          alignItems={isXs ? 'stretch' : 'center'}
        >
          <Typography variant="body2" color="text.secondary">
            Crea usuarios y define su rol de acceso.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenPanel(null)}
          >
            Nuevo usuario
          </Button>
        </Stack>

        <AppDataTable rows={usuarios} columns={columns} loading={loading} />
      </Stack>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleClosePanel}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 440 }, p: 2 },
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {formData.id_usuario ? 'Editar usuario' : 'Nuevo usuario'}
          </Typography>
          <Button startIcon={<CloseIcon />} onClick={handleClosePanel} disabled={processing}>
            Cerrar
          </Button>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Username"
              value={formData.username}
              onChange={(e) => setFormData((p) => ({ ...p, username: e.target.value }))}
              fullWidth
              required
              disabled={Boolean(formData.id_usuario)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Nombre"
              value={formData.nombre}
              onChange={(e) => setFormData((p) => ({ ...p, nombre: e.target.value }))}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Rol"
              select
              value={formData.rol}
              onChange={(e) => setFormData((p) => ({ ...p, rol: e.target.value }))}
              fullWidth
              required
            >
              {ROLES.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={formData.id_usuario ? 'Nuevo PIN (opcional)' : 'PIN'}
              type="password"
              value={formData.pin}
              onChange={(e) => setFormData((p) => ({ ...p, pin: e.target.value }))}
              fullWidth
              required={!formData.id_usuario}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.activo}
                  onChange={(e) => setFormData((p) => ({ ...p, activo: e.target.checked }))}
                />
              }
              label="Activo"
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleSubmit}
              disabled={processing}
            >
              Guardar
            </Button>
          </Grid>
        </Grid>
      </Drawer>
    </PageContainer>
  );
};

export default Usuarios;
